import { z } from 'zod';
import { contentIdSchema, imageRefSchema, mediaKindSchema, metadataSourceSchema } from './common.js';
export const genreSchema = z.object({
    id: z.string(),
    name: z.string(),
});
export const ratingsSchema = z.object({
    /** Средняя оценка 0..10, если источник её предоставил. */
    average: z.number().min(0).max(10).nullable(),
    voteCount: z.number().int().nonnegative(),
});
/**
 * Состояние доступности просмотра. Карточка контента существует даже тогда,
 * когда ни один provider не вернул поток (TMDB_INTEGRATION.md).
 */
export const playbackAvailabilitySchema = z.enum([
    'available',
    'unavailable',
    'not-configured',
    'forbidden',
]);
/** Короткая карточка для списков и каруселей. */
export const catalogItemSchema = z.object({
    id: contentIdSchema,
    kind: mediaKindSchema,
    source: metadataSourceSchema,
    title: z.string(),
    originalTitle: z.string().nullable(),
    /**
     * Язык оригинала тайтла (ISO 639-1: `en`, `ko`, `ru`). Нужен не для показа, а
     * для того, чтобы плеер узнал в списке озвучек ОРИГИНАЛЬНУЮ дорожку: в тегах
     * контейнера её помечают редко, а «всё нерусское — оригинал» объявило бы
     * оригиналом и польскую озвучку корейского сериала. `null` — витрина языка не
     * назвала.
     */
    originalLanguage: z.string().nullable().default(null),
    overview: z.string(),
    releaseYear: z.number().int().nullable(),
    poster: imageRefSchema.nullable(),
    backdrop: imageRefSchema.nullable(),
    genres: z.array(genreSchema),
    /**
     * Телепередача, а не сериал: ток-шоу, реалити, новости.
     *
     * Витрина держит их в одном списке с сериалами (`kind: 'tv'`), и «Популярные
     * сериалы» наполовину состоят из вечерних шоу. Верхний блок главной при
     * автопоказе такие тайтлы не предлагает (решение владельца): он обещает кино
     * и сериалы. Решает сервер, один раз и для всех клиентов — веб и приложение
     * только читают признак и своих списков жанров не держат.
     *
     * На сами ряды и на карточку признак не влияет: передача остаётся в
     * «Популярных сериалах», и, поставив на неё фокус, зритель увидит её наверху.
     * У фильма всегда `false`. Сервер прежней версии поля не знает — тогда
     * `false`, и автопоказ ведёт себя как раньше.
     */
    tvShow: z.boolean().default(false),
    ratings: ratingsSchema,
    playback: z.object({ availability: playbackAvailabilitySchema }),
});
/** Полная карточка контента. */
/**
 * Сезон сериала. Список сезонов приходит вместе с карточкой контента, а состав
 * серий — отдельным запросом: у длинных сериалов эпизодов сотни, и тянуть их
 * все в карточку значило бы платить трафиком за экран, где показан один сезон.
 */
export const seasonSummarySchema = z.object({
    /** Номер сезона у поставщика метаданных; `0` — спецвыпуски. */
    number: z.number().int().nonnegative(),
    name: z.string(),
    episodeCount: z.number().int().nonnegative(),
    airDate: z.string().nullable(),
    poster: imageRefSchema.nullable(),
});
/**
 * Человек в титрах: кто и кого сыграл.
 *
 * Роль отдельным полем, а не приписанной к имени: зритель ищет глазами имя, а
 * роль читает следом, и склеенная строка «Том Холланд — Питер Паркер» мешает и
 * тому и другому. Фотографии может не быть — тогда клиент рисует инициалы.
 */
export const castMemberSchema = z.object({
    id: z.string(),
    name: z.string(),
    role: z.string(),
    photo: imageRefSchema.nullable(),
});
export const catalogItemDetailsSchema = catalogItemSchema.extend({
    releaseDate: z.string().nullable(),
    runtimeMinutes: z.number().int().positive().nullable(),
    tagline: z.string().nullable(),
    /**
     * Страны производства по-русски, в порядке источника («США»,
     * «Великобритания»); не больше четырёх. Пустой список — источник не сказал.
     * Клиент пишет их в строке с годом: «2026 · США · Сериал».
     */
    countries: z.array(z.string()).default([]),
    /**
     * Внешние идентификаторы нужны server-side provider plugins.
     * TODO(providers): скрыть от неавторизованного клиента, когда появится auth.
     */
    externalIds: z.record(z.string(), z.string()),
    /** Сезоны сериала; для фильма — пустой список. */
    seasons: z.array(seasonSummarySchema).default([]),
    /**
     * Актёры в порядке значимости роли, как их отдаёт источник метаданных.
     * Пустой список — состав неизвестен, а не «актёров нет».
     */
    cast: z.array(castMemberSchema).default([]),
    /**
     * Фирменный логотип названия (title treatment) — рисуется вместо текстового
     * заголовка в крупном блоке главной и на странице тайтла.
     *
     * `width`/`height` — размеры ОРИГИНАЛА у витрины, а не отдаваемой картинки:
     * логотипы бывают и узкой строкой, и квадратным «стеком», и клиент должен
     * знать пропорции до загрузки, чтобы не прыгала вёрстка. `null` — русского
     * логотипа нет (или тайтл не из TMDB): клиент пишет название текстом.
     * Логотипы на других языках, в том числе английские, не отдаются.
     */
    logo: imageRefSchema.nullable().default(null),
});
/**
 * Ответ лёгкого маршрута `/catalog/item/:id/logo`.
 *
 * Отдельно от карточки: главная меняет тайтл в верхнем блоке при каждой смене
 * фокуса, и тянуть ради одной картинки всю карточку с титрами и сезонами было
 * бы расточительно. Форма поля та же, что у `logo` карточки.
 */
export const catalogLogoResponseSchema = z.object({
    logo: imageRefSchema.nullable().default(null),
});
/**
 * Фильмография человека: сам человек и то, где он снимался.
 *
 * Отдельный ответ, а не «поиск по имени»: имя не уникально (двух Крисов Эвансов
 * поиск смешает в одну кучу), а идентификатор актёра у нас уже есть — он лежит
 * в титрах карточки, по которым зритель и нажимает.
 */
export const personCreditsResponseSchema = z.object({
    /** Тот же id, что в титрах карточки. */
    id: z.string(),
    name: z.string(),
    photo: imageRefSchema.nullable().default(null),
    /** Чем человек известен: «Актёр», «Режиссёр». `null` — источник не сказал. */
    knownFor: z.string().nullable().default(null),
    /** Фильмы и сериалы, свежие первыми: карьеру читают с того, что сейчас. */
    items: z.array(catalogItemSchema).default([]),
    /** Честное объяснение пустого списка вместо тихой пустоты. */
    notice: z.string().nullable().default(null),
});
/** «Похожее» к карточке — рекомендации витрины. */
export const similarResponseSchema = z.object({
    contentId: contentIdSchema,
    items: z.array(catalogItemSchema).default([]),
    notice: z.string().nullable().default(null),
});
/**
 * Киновселенная тайтла: другие части той же франшизы.
 *
 * Отдельно от «похожего»: похожее — догадка витрины о вкусе, а это факт из
 * карточки («Гарри Поттер», «Матрица»), и зрителю он нужен раньше догадок.
 */
export const collectionResponseSchema = z.object({
    contentId: contentIdSchema,
    /** Название вселенной у витрины; `null` — тайтл ни в какую не входит. */
    name: z.string().nullable().default(null),
    /** Части по порядку выхода: франшизу читают с начала, а не с новинки. */
    items: z.array(catalogItemSchema).default([]),
    notice: z.string().nullable().default(null),
});
export const catalogSectionSchema = z.object({
    id: z.string(),
    title: z.string(),
    /** Подсказка для UI о форме секции: обычный ряд или hero-баннер. */
    layout: z.enum(['rail', 'hero']),
    items: z.array(catalogItemSchema),
});
export const catalogModeSchema = z.enum(['tmdb', 'demo']);
/**
 * `notice` — честное объяснение состояния каталога для UI
 * (например, «TMDB не настроен, показан демонстрационный каталог»).
 */
export const catalogHomeResponseSchema = z.object({
    mode: catalogModeSchema,
    notice: z.string().nullable(),
    sections: z.array(catalogSectionSchema),
});
export const searchQuerySchema = z.object({
    q: z.string().trim().min(1).max(120),
    page: z.coerce.number().int().min(1).max(50).default(1),
});
export const searchResponseSchema = z.object({
    mode: catalogModeSchema,
    notice: z.string().nullable(),
    query: z.string(),
    page: z.number().int().positive(),
    totalPages: z.number().int().nonnegative(),
    totalResults: z.number().int().nonnegative(),
    items: z.array(catalogItemSchema),
});
/**
 * Аниме — наш собственный жанр, которого у витрины нет.
 *
 * У витрины есть только «мультфильм», где «Симпсоны» лежат вперемешку с «Блич»:
 * пятнадцать тысяч сериалов, и аниме в них тонет. Поэтому аниме собираем сами —
 * мультфильм плюс японский язык оригинала. «Мультфильм» при этом остаётся как
 * был: это не переименование, а отдельная кнопка рядом.
 *
 * Идентификатор нарочно не числовой: так он не столкнётся с жанрами витрины,
 * даже если та когда-нибудь заведёт свой.
 */
export const ANIME_GENRE_ID = 'anime';
/** Постраничные списки `/catalog/movies` и `/catalog/tv`. TMDB отдаёт максимум 500 страниц. */
export const catalogListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).max(500).default(1),
    /**
     * Показать только этот жанр; без него — весь список.
     *
     * Идентификатор витрины (число строкой) либо наш `anime`, но не название:
     * названия переводятся и зависят от языка ответа, и фильтр по ним разъехался
     * бы при первой же смене локали.
     */
    genre: z
        .string()
        // Номер жанра начинается не с нуля: `0` и `007` — не жанры, а мусор в
        // адресе, и отвечать на них списком нельзя.
        .regex(new RegExp(`^([1-9]\\d*|${ANIME_GENRE_ID})$`), 'Неизвестный жанр')
        .optional(),
});
export const catalogListResponseSchema = z.object({
    mode: catalogModeSchema,
    notice: z.string().nullable(),
    page: z.number().int().positive(),
    totalPages: z.number().int().nonnegative(),
    totalResults: z.number().int().nonnegative(),
    items: z.array(catalogItemSchema),
    /**
     * Жанры этого раздела — для ряда кнопок над списком.
     *
     * Приходят вместе со списком, а не отдельным запросом: экран без них
     * нарисовать нельзя, а второй поход к серверу задержал бы его открытие.
     * Набор у фильмов и сериалов разный, поэтому список свой у каждого раздела.
     */
    genres: z.array(genreSchema).default([]),
    /** Какой жанр показан сейчас; `null` — показаны все. */
    genre: z.string().nullable().default(null),
});
export const episodeSchema = z.object({
    seasonNumber: z.number().int().nonnegative(),
    episodeNumber: z.number().int().positive(),
    name: z.string(),
    overview: z.string(),
    airDate: z.string().nullable(),
    runtimeMinutes: z.number().int().positive().nullable(),
    still: imageRefSchema.nullable(),
});
export const seasonEpisodesResponseSchema = z.object({
    contentId: contentIdSchema,
    seasonNumber: z.number().int().nonnegative(),
    episodes: z.array(episodeSchema),
    /** Честное объяснение пустого списка вместо тихой пустоты. */
    notice: z.string().nullable(),
});
//# sourceMappingURL=catalog.js.map